import { AccountModifyReq } from "./bindings/AccountModifyReq";
import { AddrData } from "./bindings/AddrData";
import { AddrItem } from "./bindings/AddrItem";
import { AttrId } from "./bindings/AttrId";
import { CartItem } from "./bindings/CartItem";
import { CartReq } from "./bindings/CartReq";
import { CartResponse } from "./bindings/CartResponse";
import { Currency } from "./bindings/Currency";
import { CustomerInfo } from "./bindings/CustomerInfo";
import { ImageId } from "./bindings/ImageId";
import { ImageInfo } from "./bindings/ImageInfo";
import { CountryList } from "./bindings/CountryList";
import { Item } from "./bindings/Item";
import { ItemId } from "./bindings/ItemId";
import { ItemSet } from "./bindings/ItemSet";
import { ItemsReq } from "./bindings/ItemsReq";
import { Order } from "./bindings/Order";
import { OrderListItem } from "./bindings/OrderListItem";
import { OrderPreview } from "./bindings/OrderPreview";
import { OrderReq } from "./bindings/OrderReq";
import { OrderUpdateReq } from "./bindings/OrderUpdateReq";
import { Price } from "./bindings/Price";
import { Rates } from "./bindings/Rates";
import { assert, randomUUID, toBase64 } from "./util";
import { DataContext } from "./bindings/DataContext";
import { CategoryId } from "./bindings/CategoryId";
import { AttrCounts } from "./bindings/AttrCounts";
import { AttrDefinition } from "./bindings/AttrDefinition";
import { AttrVal } from "./bindings/AttrVal";
import { Variant } from "./bindings/Variant";
import { TextValue } from "./bindings/TextValue";
import { LangCode } from "./bindings/LangCode";
import { ItemList } from "./bindings/ItemList";
import { PublicItem } from "./bindings/PublicItem";
import { CategoryReq } from "./bindings/CategoryReq";
import { UserData } from "./bindings/UserData";
import { LoginInfo } from "./bindings/LoginInfo";
import { DirEntry } from "./bindings/DirEntry";
import { UserBase } from "./bindings/UserBase";
import { Credentials } from "./bindings/Credentials";
import { ProviderInfo } from "./bindings/ProviderInfo";
import { UploadRequest } from "./bindings/UploadRequest";
import { UploadResponse } from "./bindings/UploadResponse";
import { ImportReq } from "./bindings/ImportReq";
import { ImportResponse } from "./bindings/ImportResponse";
import { LoginParams } from "./bindings/LoginParams";
import { UserIdentifier } from "./bindings/UserIdentifier";
import { FileInfo } from "./bindings/FileInfo";
import { AuthFuncs, check_fetch, SignFunc, uuid } from "./api_common";

export type UserId = number;

export type {
  PublicItem,
  ItemList,
  LangCode,
  TextValue,
  Variant,
  Rates,
  AttrDefinition,
  AttrVal,
  AttrId,
  Item,
  ItemId,
  ItemSet,
  ImageInfo,
  ImageId,
  Price,
  Currency,
  AddrData,
  CustomerInfo,
  OrderListItem,
  OrderUpdateReq,
  OrderReq,
  OrderPreview,
  Order,
  DataContext,
  LoginInfo,
  DirEntry,
  ProviderInfo,
  UserIdentifier
};

export const global_perms = ["User", "DataEntry", "Admin", "ViewLog", "Spy"];


export type CreateUserReq = UserBase & {
    creds: Credentials,
    user_data?: UserData,
};
export type AdminReq =
  { "AddUser": CreateUserReq } |
  "UserList" |
  { "UpdateUser": { id: UserId, info: UserBase, } } |
  { "SetPassword": { id: UserId, creds: Credentials, } } |
  { "SendPasswordReset": { id: UserId, } };

export type Login = LoginInfo & { f: AuthFuncs | null };
const upload_endpoint = `${location.protocol}//${location.host}/upload/`;
const url_api_base = `${location.protocol}//${location.host}/api`;
const utf8encoder = new TextEncoder();

export async function get_data_context(): Promise<DataContext> {
  return await get_json("/context");
}

export async function get_counts(category: CategoryId): Promise<AttrCounts> {
  return await get_json(`/counts/${category}`);
}

export async function get_items(req: ItemsReq): Promise<ItemSet> {
  return await post_json("/items", req);
}
export async function get_item_list(): Promise<ItemList> {
  return await get_json(`/items/list`);
}
export async function get_item(id: ItemId): Promise<Item | null> {
  return await get_json(`/item/${id}`);
}
export function image_url(id: ImageId, width: number): string {
  return `/api/img/${id}?width=${width}`;
}
export async function upload_image(file: File): Promise<ImageId> {
  const request = await check_fetch(url_api_base + "/img", {
    method: "POST",
    headers: {
      "Content-Type": file.type,
    },
    body: file,
  });
  const text = await request.text();
  return JSON.parse(text);
}
export async function add_attr(attr: AttrDefinition): Promise<AttrId> {
  return await post_json("/attr", { Add: { attr } });
}
export async function edit_attr(
  id: AttrId,
  attr: AttrDefinition,
): Promise<void> {
  await post_json_void("/attr", { Edit: { id, attr } });
}
export async function category_op(op: CategoryReq): Promise<CategoryId | null> {
  return await post_json("/category", op);
}
export async function add_item(id: ItemId, item: Item): Promise<void> {
  return await post_json_void(`/item/${id}`, item);
}
export async function delete_item(id: ItemId): Promise<void> {
  return await delete_json(`/item/${id}`);
}
export async function gen_ids(n: number): Promise<ItemId[]> {
  return await post_json("/item/gen-id", n);
}
export type Permission = "Admin" | "DataEntry" | "User" | "ViewLog" | "Spy";



export function login_basic(params: LoginParams): Promise<LoginInfo> {
  return post_json("/auth/basic/login", params);
}
export async function get_status(): Promise<LoginInfo | null> {
  const info = await get_json(`/status?uuid=${uuid()}`);
  if (info === null) return null;
  return { ...info, f: null };
}
export async function signup(email: string, crypto: string) {
  return await post_json("/acc/signup", { email, crypto, uuid: uuid() });
}
export async function request_pw_reset(email: string) {
  return await post_json("/acc/request-reset", { email, uuid: uuid() });
}
export async function admin(req: AdminReq, f: AuthFuncs): Promise<any> {
  return await post_json("/admin", req);
}
export async function country_list(): Promise<CountryList> {
  return await get_json("/country-list");
}
export async function addr_fields(id: string): Promise<AddrItem[] | null> {
  return await get_json(`/addr-fields/${id}`);
}
export async function order(req: OrderReq): Promise<Order> {
  return await post_json("/order", req);
}
export async function order_preview(req: OrderReq): Promise<OrderPreview> {
  return await post_json("/order-preview", req);
}
export async function order_update(req: OrderUpdateReq): Promise<void> {
  return await post_json_void("/order-update", req);
}
export async function acc_info(): Promise<CustomerInfo> {
  return await get_json("/acc/info");
}
export async function update_acc_info(
  info: CustomerInfo,
  f: AuthFuncs,
): Promise<void> {
  return await post_json_signed("/acc/info", info, f.sign);
}
export async function get_cart(): Promise<CartItem[]> {
  return await get_json(`/cart`);
}
export async function update_cart(req: CartReq): Promise<CartResponse> {
  return await post_json(`/cart`, req);
}
export async function orders(): Promise<OrderListItem[]> {
  return await get_json("/orders");
}
export async function get_order(id: string): Promise<Order> {
  return await get_json(`/order/${id}`);
}
export function order_url(id: string): string {
  return `${url_api_base}/order/${id}/view`;
}
export async function acc_modify(
  req: AccountModifyReq,
  f: AuthFuncs,
): Promise<void> {
  return await post_json_signed("/acc/modify", req, f.sign);
}

export type FileId = string;
export type UploadProgress = {
  partial: bigint,
  total: bigint
};
type ProgressCallback = (p: UploadProgress) => void;
export async function upload_files(files: File[], progress_cb?: ProgressCallback, complete_cb?: (data: ImportResponse) => void, publish?: boolean): Promise<FileId[]> {
  let total = 0n;
  let data: UploadRequest[] = files.map((file) => {
    total += BigInt(file.size);
    return {
      name: file.name,
      mime: file.type,
      size: file.size,
      public: publish || false,
    };
  });
  let partial = 0n;
  let tokens: UploadResponse[] = await post_json("/storage/upload", data);

  async function inner() {
    for (const [i, file] of files.entries()) {
      const { token, id } = tokens[i];
      const size = BigInt(file.size);
      let response = await check_fetch(upload_endpoint + token, {
        headers: {
          "Content-Range": `bytes 0-${size-1n}/${size}`
        },
        method: "PUT",
        body: file
      });
      partial += BigInt(file.size);
      if (progress_cb) {
        progress_cb({total, partial});
      }
      let data = await response.json();
      if (complete_cb) {
        complete_cb(data);
      }
    }
  }
  inner();
  return tokens.map(({id}) => id);
}

export function file_info(id: FileId): Promise<FileInfo> {
  return get_json(`/storage/info/${id}`);
}
export function update_file_info(id: FileId, info: FileInfo): Promise<void> {
  return post_json_void(`/storage/info/${id}`, info);
}
export type ProviderId = number;
export type Source = {
    provider: ProviderId,
    id: string
};

export async function import_file(req: ImportReq): Promise<ImportResponse> {
  return await post_json("/storage/import", req)
}

export async function log_error(error: ErrorEvent) {
  post_json_void("/log", {
    type: "error",
    user: uuid(),
    line: error.lineno,
    file: error.filename,
    message: error.message,
    error: error.error,
  });
}

async function post_body(path: string, body: ArrayBuffer): Promise<Response> {
  return await check_fetch(url_api_base + path, {
    method: "POST",
    credentials: "include",
    body,
  });
}

export async function post_json(path: string, data: any): Promise<any> {
  const request = await check_fetch(url_api_base + path, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(data),
  });
  const text = await request.text();
  return JSON.parse(text);
}
async function post_json_signed(
  path: string,
  data: any,
  sign: SignFunc,
): Promise<any> {
  const body = new Uint8Array(utf8encoder.encode(JSON.stringify(data)));
  const signature = await sign(body);
  const request = await check_fetch(url_api_base + path, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-Signature": signature,
    },
    body,
  });
  const text = await request.text();
  return JSON.parse(text);
}
export async function post_json_void(path: string, data: any): Promise<void> {
  await check_fetch(url_api_base + path, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(data),
  });
}
async function delete_json(path: string): Promise<any> {
  const request = await check_fetch(url_api_base + path, {
    method: "DELETE",
  });
  const text = await request.text();
  return JSON.parse(text);
}

export async function get_json(path: string): Promise<any> {
  const request = await check_fetch(url_api_base + path);
  const text = await request.text();

  return JSON.parse(text);
}
